import React, { useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTheme } from "next-themes";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import SectionHeader from "@/components/brams/SectionHeader";

const ACCENT = "#E10600";

const cards = [
  {
    id: "logos",
    title: "Logos & Branding",
    description:
      "Création de logos, identités visuelles et chartes graphiques modernes pour construire une image de marque forte et mémorable.",
    image:
      "https://customer-assets.emergentagent.com/job_brams-portfolio/artifacts/flybezwb_ChatGPT%20Image%2027%20juin%202026%2C%2000_24_13.png",
    effect: "zoom-glow",
    href: "/services/logos-branding",
  },
  {
    id: "print",
    title: "Flyers & Print Design",
    description:
      "Flyers, dépliants, affiches, cartes de visite, menus, bâches et tous vos supports de communication imprimés.",
    image:
      "https://customer-assets.emergentagent.com/job_brams-portfolio/artifacts/0dm28pfp_ChatGPT%20Image%2027%20juin%202026%2C%2000_58_42%20copie.jpg",
    effect: "stack",
    href: "/services/flyers-print",
  },
  {
    id: "web",
    title: "Web Design",
    description:
      "Sites vitrines, boutiques en ligne, interfaces modernes, rapides et entièrement responsives.",
    image:
      "https://customer-assets.emergentagent.com/job_brams-portfolio/artifacts/sv0qpppx_ChatGPT%20Image%2027%20juin%202026%2C%2001_06_18.png",
    effect: "parallax",
    href: "/services/web-design",
  },
  {
    id: "print-prod",
    title: "Impression",
    description:
      "Nous prenons en charge l'impression professionnelle de tous vos supports : flyers, cartes de visite, affiches, bâches, roll-ups, stickers et enseignes.",
    image:
      "https://customer-assets.emergentagent.com/job_brams-portfolio/artifacts/nhoa1gsc_ChatGPT%20Image%2027%20juin%202026%2C%2001_09_13.png",
    effect: "float-3d",
    href: "/services/impression",
  },
];

function FloatingParticles() {
  // Pure CSS subtle particles, scoped to this section
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {Array.from({ length: 18 }).map((_, i) => {
        const left = Math.random() * 100;
        const size = 1 + Math.random() * 2.5;
        const delay = Math.random() * -25;
        const duration = 22 + Math.random() * 22;
        const opacity = 0.18 + Math.random() * 0.4;
        return (
          <span
            key={i}
            className="particle bg-white"
            style={{
              left: `${left}%`,
              bottom: "-10px",
              width: `${size}px`,
              height: `${size}px`,
              animationDelay: `${delay}s`,
              animationDuration: `${duration}s`,
              opacity,
              filter: "blur(0.5px)",
            }}
          />
        );
      })}
    </div>
  );
}

function RealisationCard({ card, index, isDark, reduce }) {
  const cardRef = useRef(null);
  const navigate = useNavigate();

  // Parallax pointer tracking
  const onMouseMove = (e) => {
    if (reduce) return;
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    el.style.setProperty("--mx", `${x * 16}px`);
    el.style.setProperty("--my", `${y * 16}px`);
    el.style.setProperty("--rx", `${-y * 6}deg`);
    el.style.setProperty("--ry", `${x * 6}deg`);
  };
  const onMouseLeave = () => {
    const el = cardRef.current;
    if (!el) return;
    el.style.setProperty("--mx", `0px`);
    el.style.setProperty("--my", `0px`);
    el.style.setProperty("--rx", `0deg`);
    el.style.setProperty("--ry", `0deg`);
  };
  const onClick = () => {
    if (card.href) navigate(card.href);
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
      className="relative group"
      data-testid={`realisation-card-${card.id}`}
    >
      <div
        ref={cardRef}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
        role={card.href ? "button" : undefined}
        tabIndex={card.href ? 0 : undefined}
        onKeyDown={(e) => {
          if (card.href && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            navigate(card.href);
          }
        }}
        className={cn(
          "relative h-full overflow-hidden rounded-[24px] cursor-pointer transition-all duration-[400ms] ease-out",
          // Glassmorphism
          isDark
            ? "bg-white/[0.03] border border-white/[0.08] backdrop-blur-md"
            : "bg-black/[0.025] border border-black/[0.08] backdrop-blur-md",
          // Soft shadow
          isDark
            ? "shadow-[0_20px_60px_-30px_rgba(0,0,0,0.8)] hover:shadow-[0_30px_80px_-20px_rgba(225,6,0,0.25)]"
            : "shadow-[0_12px_40px_-20px_rgba(0,0,0,0.25)] hover:shadow-[0_20px_60px_-15px_rgba(225,6,0,0.35)]",
          // Lift
          "hover:-translate-y-2"
        )}
        style={{
          transformStyle: "preserve-3d",
          transform: "perspective(1000px) rotateX(var(--rx,0)) rotateY(var(--ry,0))",
          transition: "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.4s ease",
        }}
      >
        {/* Red glow border on hover */}
        <div
          className="absolute inset-0 rounded-[24px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          style={{
            boxShadow: `inset 0 0 0 1px ${ACCENT}55, 0 0 80px -20px ${ACCENT}66`,
          }}
        />

        {/* Image */}
        <div className="relative aspect-[16/10] overflow-hidden">
          <img
            src={card.image}
            alt={card.title}
            loading="lazy"
            className={cn(
              "absolute inset-0 w-full h-full object-cover grayscale transition-all duration-[700ms] ease-out",
              "group-hover:grayscale-0 group-hover:scale-[1.08]"
            )}
            style={{
              transform: "translate3d(var(--mx,0), var(--my,0), 0)",
              willChange: "transform",
            }}
          />
          {/* Top scrim */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.65) 100%)",
            }}
          />

          {/* Number tag */}
          <div className="absolute top-5 left-5 flex items-center gap-2">
            <span
              className="font-display text-xs tracking-[0.35em] uppercase text-white/85"
              style={{ textShadow: "0 1px 6px rgba(0,0,0,0.5)" }}
            >
              0{index + 1}
            </span>
            <span
              className="h-px w-8 transition-all duration-500 group-hover:w-14"
              style={{ background: ACCENT }}
            />
          </div>

          {/* Top right arrow */}
          <div
            className={cn(
              "absolute top-5 right-5 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500",
              "bg-white/10 border border-white/20 backdrop-blur-md",
              "group-hover:bg-white group-hover:scale-110"
            )}
            style={{
              boxShadow: "0 6px 24px rgba(0,0,0,0.25)",
            }}
          >
            <ArrowUpRight
              size={16}
              className="text-white group-hover:text-black transition-colors duration-300"
            />
          </div>
        </div>

        {/* Body */}
        <div className={cn("p-7 md:p-9", isDark ? "text-white" : "text-black")}>
          <h3 className="font-display font-semibold text-2xl md:text-3xl tracking-tighter leading-tight">
            {card.title}
          </h3>
          <p
            className={cn(
              "mt-4 text-sm md:text-[15px] leading-relaxed font-body font-light",
              isDark ? "text-white/70" : "text-black/65"
            )}
          >
            {card.description}
          </p>

          {/* Bottom accent line */}
          <div className="mt-7 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span
                className="inline-block w-1.5 h-1.5 rounded-full"
                style={{ background: ACCENT }}
              />
              <span
                className={cn(
                  "text-[10px] uppercase tracking-[0.35em] font-medium",
                  isDark ? "text-white/55" : "text-black/55"
                )}
              >
                En savoir plus
              </span>
            </div>
            <div
              className="h-px w-12 transition-all duration-500 group-hover:w-20"
              style={{ background: ACCENT }}
            />
          </div>
        </div>
      </div>
    </motion.article>
  );
}

export default function Realisations() {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const reduce = useReducedMotion();

  return (
    <section
      id="realisations"
      data-testid="realisations-section"
      className={cn(
        "relative py-28 md:py-36 overflow-hidden",
        isDark ? "bg-[#0A0A0A]" : "bg-[#fafafa]"
      )}
    >
      {/* Subtle background red accent glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          background: isDark
            ? `radial-gradient(80% 40% at 50% 0%, ${ACCENT}11 0%, transparent 60%)`
            : `radial-gradient(80% 40% at 50% 0%, ${ACCENT}0d 0%, transparent 60%)`,
        }}
      />
      <FloatingParticles />

      {/* Top divider */}
      <div
        className="absolute top-0 inset-x-0 h-px"
        style={{
          background: isDark
            ? "linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)"
            : "linear-gradient(90deg, transparent, rgba(0,0,0,0.1), transparent)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHeader
          eyebrow="Nos Expertises"
          title={
            <>
              Nos <em className="not-italic" style={{ color: ACCENT }}>expertises</em>
            </>
          }
          subtitle="Explorez nos expertises et découvrez des solutions créatives sur mesure, conçues pour renforcer votre image de marque et accompagner le développement de votre activité."
        />

        <div
          className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8"
          data-testid="realisations-grid"
        >
          {cards.map((c, i) => (
            <RealisationCard
              key={c.id}
              card={c}
              index={i}
              isDark={isDark}
              reduce={reduce}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
